/**
 * ACTIVITY-CORE (L1) — pila de deshacer EN MEMORIA de la sesión.
 *
 * Complemento UX del modelo de historial: cada acción del usuario registra su
 * mutación INVERSA (que se aplica por el write-path normal de PowerSync, nunca
 * rebobinando estado). Se vacía al recargar/cambiar de cuenta — el deshacer
 * duradero es el revert del feed (server-side, `reverted`).
 *
 * Criterio de dominio Stockpilot: el undo L1 aplica al CATÁLOGO (productos,
 * almacenes, ubicaciones) y a borradores de operaciones. Los moves confirmados
 * NO registran undo — su corrección es un move inverso explícito con motivo.
 */

export interface UndoableAction {
  /** Texto corto para el toast/UI ("Producto eliminado", …). Ya localizado. */
  label: string;
  /** Aplica la mutación inversa (write-path normal). */
  undo: () => Promise<void> | void;
}

export interface UndoStack {
  /**
   * Apila una acción. Devuelve un handle que la RETIRA de la pila si sigue
   * presente (true si la retiró): lo usa el toast para consumir "su" acción
   * sin pisar otras más recientes (y para que Ctrl+Z no la repita).
   */
  push(action: UndoableAction): () => boolean;
  /**
   * Deshace la última acción (LIFO). Devuelve la acción deshecha o null si la
   * pila está vacía. Si `undo()` lanza, la acción NO se re-encola (evita loops).
   */
  undoLast(): Promise<UndoableAction | null>;
  /** Última acción sin deshacerla (para pintar el toast). */
  peek(): UndoableAction | null;
  clear(): void;
  readonly size: number;
}

const DEFAULT_LIMIT = 50;

export function createUndoStack(limit: number = DEFAULT_LIMIT): UndoStack {
  const stack: UndoableAction[] = [];

  return {
    push(action: UndoableAction) {
      stack.push(action);
      if (stack.length > limit) {
        stack.splice(0, stack.length - limit);
      }
      return () => {
        const index = stack.indexOf(action);
        if (index === -1) {
          return false;
        }
        stack.splice(index, 1);
        return true;
      };
    },
    async undoLast() {
      const action = stack.pop();
      if (!action) {
        return null;
      }
      await action.undo();
      return action;
    },
    peek() {
      return stack.at(-1) ?? null;
    },
    clear() {
      stack.length = 0;
    },
    get size() {
      return stack.length;
    },
  };
}
